import React, {
  useCallback, useEffect, useMemo, useRef, useState, Suspense,
} from 'react';
import styled from 'styled-components';
import * as pdfjsLib from 'pdfjs-dist';
import { OVERLAY_AXIS_OPTIONS, OVERLAY_STATUS_OPTIONS } from '../../constants/pilotDrawingKinds';
import { fetchPageEntities } from '../../services/drawingGeometryApi';
import { filterComponentsForLens } from '../../utils/visibilityLenses.js';
import { sheetFrameBandToCanvas } from '../../utils/sheetFrameBbox.js';
import { findComponentsMatchingText, findComponentForEntity } from '../../utils/reviewObject.js';
import { DISCIPLINE_HATS, hatLabel } from '../../constants/disciplineHats.js';
import { VISIBILITY_LENSES, LENS_OPTIONS } from '../../utils/visibilityLenses.js';
import { DRAW_TOOLS } from '../../utils/userMarkup.js';
import { ARC_STYLES } from '../../utils/arcGeometry.js';
import { componentSupportsOffset } from '../../utils/markupTransform.js';
import UserDrawLayer from './UserDrawLayer.jsx';
import DrawToolbar from './DrawToolbar.jsx';
import ContextModifyBar from './ContextModifyBar.jsx';
import FloatingMarkupInspector from './FloatingMarkupInspector.jsx';
import { ENGINE_DEBUG_ENABLED } from '../../utils/engineDebug/engineDebugGate.js';

// Dev-only (.claude/plans/drawing-pipeline/review-and-debug/engine-debug-overlay-dev-tool-plan.md, D2). Lazy +
// code-split, not a plain static import: EngineDebugLayer.jsx calls
// `styled.div` etc. at module top-level, a side effect that would make a
// bundler include its code in this file's own chunk even though the render
// below is gated dead when ENGINE_DEBUG_ENABLED is false. Wrapping the
// import() itself behind the same static boolean means the dynamic import
// is only ever reachable -- and therefore only ever fetched -- in a dev
// build with REACT_APP_ENGINE_DEBUG=true.
const EngineDebugLayer = ENGINE_DEBUG_ENABLED
  ? React.lazy(() => import('./EngineDebugLayer.jsx'))
  : null;

pdfjsLib.GlobalWorkerOptions.workerSrc = `${window.location.origin}/pdf.worker.min.js`;

const ViewerRoot = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #525659;
`;

const FilterBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 10px 16px;
  background: rgba(255, 255, 255, 0.95);
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  align-items: center;
`;

const FilterSelect = styled.select`
  height: 32px;
  padding: 0 8px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 12px;
  background: #fff;
`;

const Legend = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  margin-left: auto;
  font-size: 11px;
  color: #555;
`;

const LegendSwatch = styled.span`
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 2px;
  margin-right: 4px;
  background: ${(p) => p.$color};
  border: 1px solid ${(p) => p.$stroke};
`;

const Viewport = styled.div`
  flex: 1;
  min-height: 0;
  overflow: hidden;
  position: relative;
  touch-action: none;
  cursor: default;
`;

const ViewerCanvas = styled.div`
  flex: 1;
  min-height: 0;
  position: relative;
  display: flex;
  flex-direction: column;
  cursor: default;
`;

const PanLayer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  will-change: transform;
`;

const Stage = styled.div`
  position: relative;
`;

const PdfCanvas = styled.canvas`
  display: block;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.35);
  background: #fff;
`;

const OverlayCanvas = styled.canvas`
  position: absolute;
  top: 0;
  left: 0;
  pointer-events: ${(p) => (p.$interactive ? 'auto' : 'none')};
  cursor: ${(p) => (p.$interactive ? 'pointer' : 'default')};
`;

const HighlightSvg = styled.svg`
  position: absolute;
  top: 0;
  left: 0;
  pointer-events: none;
`;

/**
 * Sheet-frame band proposals.
 *
 * A separate SVG layer rather than a pass over `OverlayCanvas`, for two
 * reasons. It keeps the existing label overlay's paint path byte-for-byte
 * unchanged — that canvas is cleared and repainted wholesale, so anything added
 * to it participates in every label repaint. And an outline-only band is
 * naturally expressed as stroked SVG, where "outline, never fill" is a property
 * of the markup rather than a discipline the painter has to keep observing.
 */
const SheetFrameSvg = styled.svg`
  position: absolute;
  top: 0;
  left: 0;
  pointer-events: none;
`;

const LoadingHint = styled.div`
  position: absolute;
  top: 12px;
  right: 12px;
  background: rgba(0, 0, 0, 0.55);
  color: #fff;
  padding: 6px 10px;
  border-radius: 6px;
  font-size: 11px;
  z-index: 5;
`;

const Toolbar = styled.div`
  position: absolute;
  bottom: 12px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 6px;
  align-items: center;
  flex-wrap: wrap;
  justify-content: center;
  max-width: calc(100% - 24px);
  background: rgba(255, 255, 255, 0.95);
  padding: 6px 10px;
  border-radius: 8px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.2);
  z-index: 30;
  pointer-events: auto;
`;

const ToolbarDivider = styled.span`
  width: 1px;
  height: 22px;
  background: #d1d5db;
  margin: 0 2px;
`;

const ToolbarBtn = styled.button`
  border: 1px solid ${(p) => (p.$active ? '#e97024' : '#d1d5db')};
  background: ${(p) => (p.$active ? 'rgba(233,112,36,0.12)' : '#fff')};
  color: ${(p) => (p.$active ? '#c45f12' : '#374151')};
  border-radius: 6px;
  padding: 4px 10px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  &:disabled { opacity: 0.45; cursor: not-allowed; }
`;

const ZoomReadout = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: #374151;
  min-width: 44px;
  text-align: center;
`;

const MarkupCanvas = styled.canvas`
  position: absolute;
  top: 0;
  left: 0;
  pointer-events: ${(p) => (p.$interactive ? 'auto' : 'none')};
  cursor: ${(p) => (p.$interactive ? 'pointer' : 'default')};
`;

/** SVG pattern id for the selected sheet-frame band's hatch. */
const SHEET_FRAME_HATCH_ID = 'sheet-frame-band-hatch';

const MIN_ZOOM = 0.25;
const MAX_ZOOM = 4;
const ZOOM_STEP = 1.2;

const STATUS_COLORS = {
  auto_validated: 'rgba(16,185,129,0.35)',
  user_confirmed: 'rgba(59,130,246,0.35)',
  pending_user_confirm: 'rgba(239,68,68,0.35)',
  needs_review: 'rgba(245,158,11,0.35)',
};

const STATUS_STROKES = {
  auto_validated: '#10b981',
  user_confirmed: '#3b82f6',
  pending_user_confirm: '#ef4444',
  needs_review: '#f59e0b',
};

function resolveComponentBbox(comp, ir) {
  if (Array.isArray(comp.bbox) && comp.bbox.length >= 4) return comp.bbox;
  const entities = ir?.entities || [];
  const boxes = (comp.entityIds || [])
    .map((id) => entities.find((e) => e.id === id)?.bbox)
    .filter((b) => Array.isArray(b) && b.length >= 4);
  if (boxes.length > 0) {
    let x0 = Infinity;
    let y0 = Infinity;
    let x1 = -Infinity;
    let y1 = -Infinity;
    for (const b of boxes) {
      x0 = Math.min(x0, b[0]);
      y0 = Math.min(y0, b[1]);
      x1 = Math.max(x1, b[0] + b[2]);
      y1 = Math.max(y1, b[1] + b[3]);
    }
    return [x0, y0, x1 - x0, y1 - y0];
  }
  if (Array.isArray(comp.anchor) && comp.anchor.length >= 2) {
    return [comp.anchor[0], comp.anchor[1], 48, 24];
  }
  return null;
}

function irBboxToCanvas(bbox, ir, viewport) {
  if (!bbox || !viewport) return null;
  const ph = ir?.pageSize?.height || viewport.height;
  const origin = ir?.coordinateOrigin || 'top-left';

  let pdfRect;
  if (origin === 'pdf-bottom-left' || origin === 'pdf') {
    pdfRect = [bbox[0], bbox[1], bbox[0] + bbox[2], bbox[1] + bbox[3]];
  } else {
    pdfRect = [bbox[0], ph - bbox[1] - bbox[3], bbox[0] + bbox[2], ph - bbox[1]];
  }

  const screenRect = viewport.convertToViewportRectangle(pdfRect);
  const x = Math.min(screenRect[0], screenRect[2]);
  const y = Math.min(screenRect[1], screenRect[3]);
  const w = Math.abs(screenRect[0] - screenRect[2]);
  const h = Math.abs(screenRect[1] - screenRect[3]);
  return { x, y, w: Math.max(w, 4), h: Math.max(h, 4) };
}

function matchesOverlayFilters(comp, filters) {
  if (filters.status !== 'all' && comp.mappingStatus !== filters.status) return false;
  if (filters.axis !== 'all' && comp.taxonomyAxis !== filters.axis) return false;
  if (filters.kind !== 'all' && comp.kind !== filters.kind) return false;
  return true;
}

function pdfPointToCanvas(x, y, viewport) {
  const [sx, sy] = viewport.convertToViewportPoint(x, y);
  return { x: sx, y: sy };
}

function clampZoom(z) {
  return Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, z));
}

export default function DrawingViewer({
  pdfUrl,
  pdfDocExternal,
  onPdfDocReady,
  ir,
  entityPages,
  highlightComponentId,
  previewComponentId,
  previewPage,
  onComponentClick,
  onPageChange,
  showOverlays = true,
  showIrLabels = false,
  showVectorMarkups = false,
  visibilityLens,
  onVisibilityLensChange,
  activeHatId,
  onHatChange,
  issueHighlightOnly = false,
  issueHighlightText,
  overlayFilters,
  onOverlayFiltersChange,
  emptyMessage,
  initialPage = 1,
  onViewportInfoChange,
  drawTool = DRAW_TOOLS.SELECT,
  onDrawToolChange,
  userMarkups = [],
  selectedUserMarkupId = null,
  selectedUserMarkup = null,
  showDrawChrome = true,
  showFloatingInspector = true,
  onUserMarkupComplete,
  onUserMarkupSelect,
  onUserMarkupUpdate,
  onUserMarkupSave,
  onUserMarkupDelete,
  onUserMarkupHistoryCheckpoint,
  onDismissFloatingInspector,
  onOpenFullInspector,
  modifyMode = null,
  onModifyModeChange,
  onCopyUserMarkup,
  onRotateUserMarkup,
  onMirrorUserMarkup,
  onOffsetUserMarkup,
  intentPromptMarkupId = null,
  scaleRatio = null,
  selectedExtractedComponent = null,
  onExtractedGeometryUpdate,
  userDrawSheetId = '',
  // Sheet frame band review. `sheetFrameSelectedProposal` is null until the
  // reviewer explicitly picks a row -- there is no overlay before that, because
  // a band drawn without being asked for reads as something the detector has
  // already decided.
  sheetFrameSelectedProposal = null,
  sheetFrameProposals = [],
  sheetFramePageEvidence = null,
}) {
  const canvasRef = useRef(null);
  const overlayCanvasRef = useRef(null);
  const markupCanvasRef = useRef(null);
  const stageRef = useRef(null);
  const viewportRef = useRef(null);
  const viewerCanvasRef = useRef(null);
  const scrollPauseTimer = useRef(null);
  const overlayPaintRef = useRef(null);
  const fitScaleRef = useRef(1);
  const userDrawLayerRef = useRef(null);
  const [drawDraft, setDrawDraft] = useState({
    active: false, pointCount: 0, canFinish: false, hint: '', tool: DRAW_TOOLS.SELECT,
  });
  const [arcDrawMode, setArcDrawMode] = useState(ARC_STYLES.CIRCULAR);
  const [dimensionInput, setDimensionInput] = useState('');
  const panState = useRef(null);
  const centerPendingRef = useRef(true);
  const panRef = useRef({ x: 0, y: 0 });
  const externalDocRef = useRef(null);

  const [docLoading, setDocLoading] = useState(true);
  const [pageRendering, setPageRendering] = useState(false);
  const [overlaysPaused, setOverlaysPaused] = useState(false);
  const [overlaysReady, setOverlaysReady] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [showMarkups, setShowMarkups] = useState(false);

  useEffect(() => {
    setDimensionInput('');
  }, [drawTool]);

  const handleCommandCancel = useCallback(() => {
    setDimensionInput('');
    onDrawToolChange?.(DRAW_TOOLS.SELECT);
    onModifyModeChange?.(null);
  }, [onDrawToolChange, onModifyModeChange]);

  useEffect(() => {
    panRef.current = pan;
  }, [pan]);

  const zoomLevelRef = useRef(zoomLevel);
  useEffect(() => {
    zoomLevelRef.current = zoomLevel;
  }, [zoomLevel]);
  const [pdfDoc, setPdfDoc] = useState(null);
  const [pageNum, setPageNum] = useState(1);
  const [numPages, setNumPages] = useState(1);
  const [canvasSize, setCanvasSize] = useState({ w: 0, h: 0 });
  const [viewport, setViewport] = useState(null);
  const [pageEntities, setPageEntities] = useState([]);
  const [localFilters, setLocalFilters] = useState({
    status: 'all',
    axis: 'all',
    kind: 'all',
  });

  const filters = overlayFilters || localFilters;
  const setFilters = onOverlayFiltersChange || setLocalFilters;
  const labelsVisible = showOverlays && (
    showIrLabels
    || Boolean(previewComponentId)
    || (issueHighlightOnly && Boolean(highlightComponentId))
  );
  const markupsVisible = showOverlays && showVectorMarkups && !previewComponentId;

  const irForPage = useMemo(
    () => (ir ? { ...ir, entities: pageEntities } : null),
    [ir, pageEntities],
  );

  useEffect(() => {
    if (!entityPages?.length || !ir) {
      setPageEntities([]);
      return undefined;
    }
    const pageIndex = pageNum - 1;
    const shard = entityPages.find((p) => p.pageIndex === pageIndex);
    if (!shard?.url) {
      setPageEntities([]);
      return undefined;
    }
    let cancelled = false;
    fetchPageEntities(shard)
      .then((entities) => { if (!cancelled) setPageEntities(entities); })
      .catch((err) => {
        console.warn('[DrawingViewer] page entities load failed:', err);
        if (!cancelled) setPageEntities([]);
      });
    return () => { cancelled = true; };
  }, [entityPages, ir, pageNum]);

  // If parent already loaded the doc (shared with thumbnail panel), adopt it directly.
  // When pdfDocExternal goes null (sheet switch), clear pdfDoc immediately so the
  // render effect never calls getPage() on a stale/destroyed document.
  useEffect(() => {
    if (!pdfDocExternal) {
      externalDocRef.current = null;
      setPdfDoc(null);
      return;
    }
    if (externalDocRef.current === pdfDocExternal) return;
    externalDocRef.current = pdfDocExternal;
    setPdfDoc(pdfDocExternal);
    setNumPages(pdfDocExternal.numPages);
    const target = previewPage ?? initialPage ?? 1;
    setPageNum(Math.min(Math.max(1, target), pdfDocExternal.numPages || 1));
    setDocLoading(false);
    setOverlaysReady(false);
  }, [pdfDocExternal, initialPage, previewPage]);

  useEffect(() => {
    if (!pdfUrl || pdfDocExternal) return undefined;
    let cancelled = false;
    // Track whether the doc was successfully handed off to the parent via
    // onPdfDocReady.  Once adopted, the parent owns the doc's lifetime and the
    // cleanup below must NOT call loadingTask.destroy() — doing so would tear
    // down the internal WorkerTransport on an already-resolved doc and cause the
    // "Cannot read properties of null (reading 'sendWithPromise')" crash the next
    // time getPage() is called.
    let adopted = false;
    setDocLoading(true);
    setPdfDoc(null);
    setOverlaysReady(false);

    const loadingTask = pdfjsLib.getDocument({
      url: pdfUrl,
      // Range requests: PDF.js fetches only what's needed for the current page.
      // S3 presigned URLs support Accept-Ranges, so this cuts download from the
      // full file size down to a few hundred KB per page view.
      rangeChunkSize: 65536,
      disableAutoFetch: true,
      disableStream: false,
    });

    loadingTask.promise
      .then((doc) => {
        if (cancelled) {
          // Load finished but URL/external changed before we could use it —
          // destroy this doc since nobody else will own it.
          doc.destroy();
          return;
        }
        adopted = true;
        setPdfDoc(doc);
        setNumPages(doc.numPages);
        setPageNum(Math.min(Math.max(1, initialPage || 1), doc.numPages || 1));
        onPdfDocReady?.(doc);
      })
      .catch((err) => {
        // "Worker was destroyed" fires when the component unmounts mid-load
        // and we called loadingTask.destroy() in the cleanup below — that is
        // expected behaviour, not an error worth logging.
        if (cancelled || err?.message?.includes('Worker was destroyed')) return;
        console.error('[DrawingViewer] load error:', err);
      })
      // Always clear docLoading — even on cancellation — so the spinner never
      // gets permanently stuck.
      .finally(() => { setDocLoading(false); });

    return () => {
      cancelled = true;
      // Only destroy the loadingTask while the promise is still pending.
      // After the doc has been handed off (adopted = true), the parent's
      // sharedPdfDoc state owns the document; destroying the task here would
      // also wipe out the WorkerTransport on that live document.
      if (!adopted) loadingTask.destroy?.();
    };
  // pdfDocExternal intentionally omitted — we use it as a gate check only.
  // Including it would re-run (and cancel) this effect every time the parent
  // feeds the loaded doc back as pdfDocExternal, which is exactly the race we
  // are fixing.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pdfUrl]);

  const goToPage = useCallback((next) => {
    if (!pdfDoc) return;
    const page = Math.min(Math.max(1, next), pdfDoc.numPages || 1);
    setPageNum(page);
    onPageChange?.(page);
  }, [pdfDoc, onPageChange]);

  // Parent-driven page (mapping preview or thumbnail) — do not echo onPageChange.
  useEffect(() => {
    if (!pdfDoc) return;
    const target = previewPage ?? initialPage;
    if (!target) return;
    const page = Math.min(Math.max(1, target), pdfDoc.numPages || 1);
    setPageNum((p) => (p === page ? p : page));
  }, [previewPage, initialPage, pdfDoc]);

  useEffect(() => {
    if (!pdfDoc || !canvasRef.current) return undefined;
    let renderTask;
    let cancelled = false;

    const render = async () => {
      setPageRendering(true);
      setOverlaysReady(false);
      try {
        const page = await pdfDoc.getPage(pageNum);
        if (cancelled) return;
        const baseVp = page.getViewport({ scale: 1 });
        // Guard against zero-width container (happens before first paint) —
        // a zero or negative parentW produces a negative scale which hangs
        // the PDF.js render task and leaves "Rendering page…" stuck forever.
        const rawW = viewportRef.current?.clientWidth
          || stageRef.current?.parentElement?.clientWidth
          || 0;
        const parentW = rawW > 60 ? rawW : 800;
        const fitScale = Math.min((parentW - 48) / baseVp.width, 2.5);
        fitScaleRef.current = fitScale;
        // Cap DPI at 1.5 to avoid enormous canvases on retina displays.
        const dpr = Math.min(window.devicePixelRatio || 1, 1.5);
        const scale = fitScale * zoomLevel * dpr;
        const vp = page.getViewport({ scale });
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        canvas.width = vp.width;
        canvas.height = vp.height;
        // CSS size stays at logical pixels so overlays line up correctly.
        canvas.style.width = `${vp.width / dpr}px`;
        canvas.style.height = `${vp.height / dpr}px`;
        setCanvasSize({ w: vp.width / dpr, h: vp.height / dpr });
        setViewport(page.getViewport({ scale: fitScale * zoomLevel }));
        renderTask = page.render({ canvasContext: ctx, viewport: vp });
        await renderTask.promise;
        if (!cancelled) setOverlaysReady(true);
      } catch (err) {
        if (err?.name !== 'RenderingCancelledException') {
          console.error('[DrawingViewer] render error:', err);
        }
      } finally {
        // Always clear the spinner — even when cancelled.  If the cleanup
        // triggered a new render, that render will immediately re-set
        // pageRendering=true, so there is no visible flash.  Without this
        // unconditional clear, cancelling a mid-flight render leaves the
        // "Rendering page…" hint stuck on screen permanently.
        setPageRendering(false);
      }
    };
    render();
    return () => {
      cancelled = true;
      renderTask?.cancel?.();
    };
  }, [pdfDoc, pageNum, zoomLevel]);

  const pauseOverlaysBriefly = useCallback(() => {
    setOverlaysPaused(true);
    if (scrollPauseTimer.current) clearTimeout(scrollPauseTimer.current);
    scrollPauseTimer.current = setTimeout(() => setOverlaysPaused(false), 200);
  }, []);

  const centerSheetInViewport = useCallback(() => {
    const vp = viewportRef.current;
    if (!vp || !canvasSize.w) return;
    setPan({
      x: (vp.clientWidth - canvasSize.w) / 2,
      y: Math.max(48, (vp.clientHeight - canvasSize.h) / 2),
    });
  }, [canvasSize.w, canvasSize.h]);

  /** Keep the sheet point under (clientX, clientY) fixed while changing zoom. */
  const zoomAroundClientPoint = useCallback((newZoom, clientX, clientY) => {
    const vp = viewportRef.current;
    const oldZoom = zoomLevelRef.current;
    const clamped = clampZoom(newZoom);
    if (clamped === oldZoom) return;

    pauseOverlaysBriefly();

    if (!vp) {
      setZoomLevel(clamped);
      return;
    }

    const rect = vp.getBoundingClientRect();
    const mx = clientX - rect.left;
    const my = clientY - rect.top;
    const ratio = clamped / oldZoom;
    const currentPan = panRef.current;

    setZoomLevel(clamped);
    setPan({
      x: mx - (mx - currentPan.x) * ratio,
      y: my - (my - currentPan.y) * ratio,
    });
  }, [pauseOverlaysBriefly]);

  useEffect(() => {
    centerPendingRef.current = true;
    setPan({ x: 0, y: 0 });
  }, [pageNum, pdfUrl]);

  useEffect(() => {
    if (!centerPendingRef.current || !canvasSize.w || !overlaysReady) return;
    centerPendingRef.current = false;
    centerSheetInViewport();
  }, [canvasSize.w, canvasSize.h, overlaysReady, centerSheetInViewport]);

  useEffect(() => {
    const vp = viewportRef.current;
    if (!vp || !pdfUrl) return undefined;

    const isInViewport = (target) => {
      if (!(target instanceof Node)) return false;
      return vp.contains(target);
    };

    const applyPan = (dx, dy) => {
      if (!panState.current) return;
      pauseOverlaysBriefly();
      setPan({
        x: panState.current.originX + dx,
        y: panState.current.originY + dy,
      });
    };

    const onWheel = (e) => {
      if (!isInViewport(e.target)) return;
      if (e.deltaY === 0) return;
      e.preventDefault();
      e.stopPropagation();
      const factor = e.deltaY > 0 ? 1 / ZOOM_STEP : ZOOM_STEP;
      zoomAroundClientPoint(zoomLevelRef.current * factor, e.clientX, e.clientY);
    };

    const endPan = () => {
      if (!panState.current) return;
      panState.current = null;
      vp.style.cursor = '';
    };

    const startPan = (clientX, clientY) => {
      panState.current = {
        startX: clientX,
        startY: clientY,
        originX: panRef.current.x,
        originY: panRef.current.y,
      };
      vp.style.cursor = 'grabbing';
    };

    const onPointerDown = (e) => {
      if (!isInViewport(e.target)) return;
      if (e.button !== 1) return;
      e.preventDefault();
      e.stopPropagation();
      startPan(e.clientX, e.clientY);
      try {
        vp.setPointerCapture(e.pointerId);
      } catch (_) {
        // ignore
      }
    };

    const onPointerMove = (e) => {
      if (!panState.current) return;
      if ((e.buttons & 4) === 0) {
        endPan();
        return;
      }
      e.preventDefault();
      applyPan(
        e.clientX - panState.current.startX,
        e.clientY - panState.current.startY,
      );
    };

    const onPointerUp = (e) => {
      if (e.button !== 1) return;
      endPan();
    };

    const onMouseDown = (e) => {
      if (!isInViewport(e.target)) return;
      if (e.button !== 1) return;
      e.preventDefault();
      e.stopPropagation();
      startPan(e.clientX, e.clientY);
    };

    const onMouseMove = (e) => {
      if (!panState.current) return;
      if ((e.buttons & 4) === 0) {
        endPan();
        return;
      }
      e.preventDefault();
      applyPan(
        e.clientX - panState.current.startX,
        e.clientY - panState.current.startY,
      );
    };

    const onMouseUp = (e) => {
      if (e.button !== 1) return;
      endPan();
    };

    const blockMiddleClick = (e) => {
      if (e.button === 1 && isInViewport(e.target)) e.preventDefault();
    };

    window.addEventListener('wheel', onWheel, { passive: false, capture: true });
    window.addEventListener('pointerdown', onPointerDown, { capture: true });
    window.addEventListener('pointermove', onPointerMove, { capture: true });
    window.addEventListener('pointerup', onPointerUp, { capture: true });
    window.addEventListener('pointercancel', endPan, { capture: true });
    window.addEventListener('mousedown', onMouseDown, { capture: true });
    window.addEventListener('mousemove', onMouseMove, { capture: true });
    window.addEventListener('mouseup', onMouseUp, { capture: true });
    window.addEventListener('auxclick', blockMiddleClick, { capture: true });

    return () => {
      window.removeEventListener('wheel', onWheel, { capture: true });
      window.removeEventListener('pointerdown', onPointerDown, { capture: true });
      window.removeEventListener('pointermove', onPointerMove, { capture: true });
      window.removeEventListener('pointerup', onPointerUp, { capture: true });
      window.removeEventListener('pointercancel', endPan, { capture: true });
      window.removeEventListener('mousedown', onMouseDown, { capture: true });
      window.removeEventListener('mousemove', onMouseMove, { capture: true });
      window.removeEventListener('mouseup', onMouseUp, { capture: true });
      window.removeEventListener('auxclick', blockMiddleClick, { capture: true });
      endPan();
    };
  }, [pauseOverlaysBriefly, pdfUrl, zoomAroundClientPoint]);

  const zoomIn = useCallback(() => {
    const vp = viewportRef.current;
    const next = zoomLevelRef.current * ZOOM_STEP;
    if (!vp) {
      setZoomLevel(clampZoom(next));
      return;
    }
    const rect = vp.getBoundingClientRect();
    zoomAroundClientPoint(next, rect.left + rect.width / 2, rect.top + rect.height / 2);
  }, [zoomAroundClientPoint]);

  const zoomOut = useCallback(() => {
    const vp = viewportRef.current;
    const next = zoomLevelRef.current / ZOOM_STEP;
    if (!vp) {
      setZoomLevel(clampZoom(next));
      return;
    }
    const rect = vp.getBoundingClientRect();
    zoomAroundClientPoint(next, rect.left + rect.width / 2, rect.top + rect.height / 2);
  }, [zoomAroundClientPoint]);

  const zoomFit = useCallback(() => {
    setZoomLevel(1);
    centerPendingRef.current = true;
  }, []);

  const zoomPercent = Math.round(zoomLevel * 100);

  useEffect(() => {
    onViewportInfoChange?.({
      pageNum,
      numPages,
      zoomPercent,
      pageSize: ir?.pageSize,
    });
  }, [onViewportInfoChange, pageNum, numPages, zoomPercent, ir?.pageSize]);

  useEffect(() => () => {
    if (scrollPauseTimer.current) clearTimeout(scrollPauseTimer.current);
    if (overlayPaintRef.current) cancelAnimationFrame(overlayPaintRef.current);
  }, []);

  const issueMatchIds = useMemo(() => {
    if (!issueHighlightText || !ir?.components) return new Set();
    const pageIndex = pageNum - 1;
    const matches = findComponentsMatchingText(ir.components, issueHighlightText, pageIndex);
    return new Set(matches.map((c) => c.id));
  }, [issueHighlightText, ir?.components, pageNum]);

  const crossRefComponentIds = useMemo(() => {
    if (!ir?.crossRefs?.length) return new Set();
    return new Set(ir.crossRefs.filter((r) => r.targetSheet).map((r) => r.componentId));
  }, [ir?.crossRefs]);

  const kindOptions = useMemo(() => {
    const kinds = new Set((ir?.components || []).map((c) => c.kind).filter(Boolean));
    return ['all', ...Array.from(kinds).sort()];
  }, [ir]);

  const overlayComponents = useMemo(() => {
    if (!viewport) return [];
    const irCtx = irForPage || ir;
    if (!irCtx?.components) return [];

    const singleComponentOverlay = (comp) => {
      const compPage = (comp.pageIndex ?? irCtx.pageIndex ?? 0) + 1;
      if (pageNum !== compPage) return null;
      const bbox = resolveComponentBbox(comp, irCtx);
      const rect = bbox ? irBboxToCanvas(bbox, irCtx, viewport) : null;
      return rect ? { comp, bbox, rect } : null;
    };

    if (previewComponentId) {
      const preview = irCtx.components.find((c) => c.id === previewComponentId);
      if (!preview) return [];
      const compPage = previewPage ?? (preview.pageIndex ?? irCtx.pageIndex ?? 0) + 1;
      if (pageNum !== compPage) return [];
      const bbox = resolveComponentBbox(preview, irCtx);
      const rect = bbox ? irBboxToCanvas(bbox, irCtx, viewport) : null;
      return rect ? [{ comp: preview, bbox, rect }] : [];
    }

    if (issueHighlightOnly) {
      const pageIndex = pageNum - 1;
      let matches = [];
      if (highlightComponentId) {
        const comp = irCtx.components.find((c) => c.id === highlightComponentId);
        if (comp) matches = [comp];
      } else if (issueHighlightText) {
        matches = findComponentsMatchingText(irCtx.components, issueHighlightText, pageIndex);
      }
      return matches
        .map((comp) => singleComponentOverlay(comp))
        .filter(Boolean);
    }

    if (!showIrLabels) return [];

    const onPage = irCtx.components.filter((c) => {
      const compPage = (c.pageIndex ?? irCtx.pageIndex ?? 0) + 1;
      return pageNum === compPage;
    });

    const lensFiltered = filterComponentsForLens(onPage, {
      visibilityLens,
      highlightComponentId,
      activeHatId,
      labelFilters: filters,
      sheetIr: irCtx,
    });

    const pool = lensFiltered.length > 0 || visibilityLens === 'hat'
      ? lensFiltered
      : onPage;

    return pool
      .filter((c) => matchesOverlayFilters(c, filters))
      .map((c) => {
        const bbox = resolveComponentBbox(c, irCtx);
        const rect = bbox ? irBboxToCanvas(bbox, irCtx, viewport) : null;
        return rect ? { comp: c, bbox, rect } : null;
      })
      .filter(Boolean);
  }, [
    activeHatId,
    highlightComponentId,
    issueHighlightOnly,
    issueHighlightText,
    irForPage,
    ir,
    filters,
    viewport,
    pageNum,
    previewComponentId,
    previewPage,
    showIrLabels,
    visibilityLens,
  ]);

  const paintOverlays = useCallback(() => {
    const canvas = overlayCanvasRef.current;
    if (!canvas || !viewport || !labelsVisible || !ir) {
      if (overlayCanvasRef.current) {
        const ctx = overlayCanvasRef.current.getContext('2d');
        ctx.clearRect(0, 0, overlayCanvasRef.current.width, overlayCanvasRef.current.height);
      }
      return;
    }
    canvas.width = canvasSize.w;
    canvas.height = canvasSize.h;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    for (const { comp, rect } of overlayComponents) {
      const status = comp.mappingStatus || 'needs_review';
      const isHi = comp.id === highlightComponentId || comp.id === previewComponentId;
      const isIssueMatch = issueMatchIds.has(comp.id);
      ctx.fillStyle = STATUS_COLORS[status] || 'rgba(233,112,36,0.25)';
      ctx.strokeStyle = isHi ? '#e97024' : isIssueMatch ? '#7c3aed' : (STATUS_STROKES[status] || '#e97024');
      ctx.lineWidth = isHi || isIssueMatch ? 3 : 1.5;
      ctx.beginPath();
      if (typeof ctx.roundRect === 'function') {
        ctx.roundRect(rect.x, rect.y, rect.w, rect.h, 3);
      } else {
        ctx.rect(rect.x, rect.y, rect.w, rect.h);
      }
      ctx.fill();
      ctx.stroke();
    }
  }, [canvasSize.h, canvasSize.w, highlightComponentId, ir, issueMatchIds, labelsVisible, overlayComponents, previewComponentId, viewport]);

  const paintMarkups = useCallback(() => {
    const canvas = markupCanvasRef.current;
    if (!canvas || !viewport) return;
    canvas.width = canvasSize.w;
    canvas.height = canvasSize.h;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    if (!markupsVisible || !pageEntities.length) return;

    for (const ent of pageEntities) {
      if (ent.type === 'polyline' && ent.points?.length >= 2) {
        const isCircle = ent.shapeKind === 'circle';
        const isTriangle = ent.shapeKind === 'triangle';
        ctx.beginPath();
        ent.points.forEach((pt, idx) => {
          const { x, y } = pdfPointToCanvas(pt[0], pt[1], viewport);
          if (idx === 0) ctx.moveTo(x, y);
          else ctx.lineTo(x, y);
        });
        if (ent.closed) ctx.closePath();
        ctx.strokeStyle = isCircle ? 'rgba(59,130,246,0.85)' : isTriangle ? 'rgba(16,185,129,0.85)' : 'rgba(148,163,184,0.55)';
        ctx.lineWidth = isCircle || isTriangle ? 1.75 : 1;
        ctx.stroke();
      }
    }
  }, [canvasSize.h, canvasSize.w, markupsVisible, pageEntities, viewport]);

  useEffect(() => {
    if (!overlaysReady || overlaysPaused || !labelsVisible || !ir) return undefined;
    overlayPaintRef.current = requestAnimationFrame(() => {
      paintOverlays();
    });
    return () => {
      if (overlayPaintRef.current) cancelAnimationFrame(overlayPaintRef.current);
    };
  }, [overlaysReady, overlaysPaused, paintOverlays, labelsVisible, ir]);

  useEffect(() => {
    if (!overlaysReady || overlaysPaused || !markupsVisible) return undefined;
    const id = requestAnimationFrame(() => paintMarkups());
    return () => cancelAnimationFrame(id);
  }, [overlaysReady, overlaysPaused, paintMarkups, markupsVisible]);

  const handleOverlayClick = useCallback((e) => {
    if (!onComponentClick || overlaysPaused || !labelsVisible) return;
    const canvas = overlayCanvasRef.current;
    if (!canvas) return;
    const bounds = canvas.getBoundingClientRect();
    const x = e.clientX - bounds.left;
    const y = e.clientY - bounds.top;
    for (let i = overlayComponents.length - 1; i >= 0; i -= 1) {
      const { comp, rect } = overlayComponents[i];
      if (x >= rect.x && x <= rect.x + rect.w && y >= rect.y && y <= rect.y + rect.h) {
        onComponentClick(comp);
        return;
      }
    }
  }, [onComponentClick, overlayComponents, overlaysPaused, labelsVisible]);

  const handleEntityClick = useCallback((e) => {
    if (!onComponentClick || overlaysPaused || !markupsVisible || !ir?.components?.length) return;
    const canvas = markupCanvasRef.current || canvasRef.current;
    if (!canvas || !viewport) return;
    const bounds = canvas.getBoundingClientRect();
    const x = e.clientX - bounds.left;
    const y = e.clientY - bounds.top;

    for (let i = pageEntities.length - 1; i >= 0; i -= 1) {
      const ent = pageEntities[i];
      if (!Array.isArray(ent.bbox) || ent.bbox.length < 4) continue;
      const rect = irBboxToCanvas(ent.bbox, irForPage || ir, viewport);
      if (!rect) continue;
      if (x >= rect.x && x <= rect.x + rect.w && y >= rect.y && y <= rect.y + rect.h) {
        const parent = findComponentForEntity(ir.components, ent.id);
        if (parent) onComponentClick(parent);
        return;
      }
    }
  }, [onComponentClick, overlaysPaused, markupsVisible, ir, irForPage, pageEntities, viewport]);

  const highlightRect = useMemo(() => {
    if (!highlightComponentId || overlaysPaused || !labelsVisible) return null;
    const hit = overlayComponents.find(({ comp }) => comp.id === highlightComponentId);
    return hit?.rect || null;
  }, [highlightComponentId, labelsVisible, overlayComponents, overlaysPaused]);

  /**
   * The selected band, plus the unselected ones drawn as faint context.
   *
   * Everything comes from `sheetFramePageEvidence` — that page's own
   * `displayedPageBounds` and `rotation`. `ir` is never consulted here: its
   * `pageSize` is set once from page 0 for the whole document, so using it
   * would silently mis-transform any band on a differently-sized page (design
   * review §1.6, S5).
   *
   * Returns `null` — and therefore renders no layer at all — until the reviewer
   * has explicitly selected a proposal.
   */
  const sheetFrameOverlay = useMemo(() => {
    if (!sheetFrameSelectedProposal || !viewport || overlaysPaused) return null;
    if (!sheetFramePageEvidence) return null;

    // EXACT-PAGE SCOPING, enforced where the band is drawn rather than trusted
    // from the parent. The evidence names the page it was measured on, and this
    // viewer knows the page it is showing; if they disagree, the band belongs
    // to a different page and is not drawn.
    //
    // The parent clears its selection when the page changes, but that clearing
    // is a separate component's effect and therefore lands a render later. In
    // that gap the previous page's band would otherwise be painted over the new
    // one -- and the page-size cross-check inside `sheetFrameBandToCanvas`
    // cannot catch it, because consecutive pages of a drawing set are almost
    // always the same size. `pageNum` is 1-based; `pageIndex` is 0-based.
    if (sheetFramePageEvidence.pageIndex !== pageNum - 1) return null;

    const selectedRect = sheetFrameBandToCanvas(
      sheetFrameSelectedProposal.bbox, sheetFramePageEvidence, viewport,
    );
    // If the SELECTED band cannot be placed with confidence, draw nothing at
    // all rather than showing only its neighbours -- a lone context outline
    // would read as the selection.
    if (!selectedRect) return null;

    const selectedFingerprint = sheetFrameSelectedProposal.authorizationFingerprint;
    const others = [];
    for (const proposal of sheetFrameProposals) {
      if (!proposal || proposal.authorizationFingerprint === selectedFingerprint) continue;
      const rect = sheetFrameBandToCanvas(proposal.bbox, sheetFramePageEvidence, viewport);
      if (rect) others.push({ key: proposal.authorizationFingerprint, rect });
    }
    return { selectedRect, others };
  }, [
    overlaysPaused,
    pageNum,
    sheetFramePageEvidence,
    sheetFrameProposals,
    sheetFrameSelectedProposal,
    viewport,
  ]);

  const visibleCount = overlayComponents.length;
  const totalCount = ir?.components?.length || 0;
  const interactiveOverlays = Boolean(onComponentClick) && !overlaysPaused && !previewComponentId && showIrLabels && labelsVisible;
  const interactiveVectors = Boolean(onComponentClick) && !overlaysPaused && markupsVisible;
  const userDrawActive = drawTool && drawTool !== DRAW_TOOLS.SELECT;
  const showUserLayer = Boolean(
    userMarkups?.length > 0
    || userDrawActive
    || selectedUserMarkupId,
  );

  const drawSnapSources = useMemo(() => ({
    components: ir?.components || [],
    userMarkups: userMarkups || [],
    pageEntities: pageEntities || [],
  }), [ir?.components, userMarkups, pageEntities]);

  if (!pdfUrl) {
    return (
      <ViewerRoot>
        <Viewport>
          <div style={{ color: '#e5e7eb', padding: 40, maxWidth: 420, lineHeight: 1.6, textAlign: 'center', margin: '0 auto' }}>
            {emptyMessage || 'No drawing loaded — select a sheet from the panel on the right.'}
          </div>
        </Viewport>
      </ViewerRoot>
    );
  }

  return (
    <ViewerRoot>
      {showOverlays && ir && showIrLabels && !previewComponentId && !issueHighlightOnly && (
        <FilterBar>
          <FilterSelect
            value={filters.status}
            onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
          >
            {OVERLAY_STATUS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </FilterSelect>
          <FilterSelect
            value={filters.axis}
            onChange={(e) => setFilters((f) => ({ ...f, axis: e.target.value }))}
          >
            {OVERLAY_AXIS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </FilterSelect>
          <FilterSelect
            value={filters.kind}
            onChange={(e) => setFilters((f) => ({ ...f, kind: e.target.value }))}
          >
            {kindOptions.map((k) => (
              <option key={k} value={k}>{k === 'all' ? 'All kinds' : k}</option>
            ))}
          </FilterSelect>
          {onHatChange && (
            <FilterSelect
              value={activeHatId || 'architect'}
              onChange={(e) => onHatChange(e.target.value)}
              title="Discipline lens"
            >
              {DISCIPLINE_HATS.map((h) => (
                <option key={h.id} value={h.id}>{h.label}</option>
              ))}
            </FilterSelect>
          )}
          <span style={{ fontSize: 12, color: '#666' }}>
            {visibleCount}/{totalCount} labels
            {activeHatId && activeHatId !== 'architect' && (
              <> · {hatLabel(activeHatId)}</>
            )}
            {(ir?.crossRefs?.length > 0) && ` · ${ir.crossRefs.length} cross-refs`}
            {overlaysPaused && ' · overlays hidden while scrolling'}
            {showVectorMarkups && ' · vectors on'}
          </span>
          <Legend>
            <span><LegendSwatch $color={STATUS_COLORS.auto_validated} $stroke={STATUS_STROKES.auto_validated} />Auto</span>
            <span><LegendSwatch $color={STATUS_COLORS.pending_user_confirm} $stroke={STATUS_STROKES.pending_user_confirm} />Unknown</span>
            <span><LegendSwatch $color={STATUS_COLORS.needs_review} $stroke={STATUS_STROKES.needs_review} />Review</span>
          </Legend>
        </FilterBar>
      )}
      <ViewerCanvas ref={viewerCanvasRef}>
        {showDrawChrome && onDrawToolChange && (
          <DrawToolbar
            activeTool={drawTool}
            onToolChange={onDrawToolChange}
            arcDrawMode={arcDrawMode}
            onArcDrawModeChange={setArcDrawMode}
            draftVisible={drawDraft.active}
            draftHint={drawDraft.hint}
            draftPointCount={drawDraft.pointCount}
            draftCanFinish={drawDraft.canFinish}
            showDimensionInput={drawDraft.showDimensionInput}
            dimensionInput={dimensionInput}
            onDimensionInputChange={setDimensionInput}
            dimensionPlaceholder={drawDraft.dimensionPlaceholder ?? (scaleRatio ? "10 or 10'" : 'pt')}
            dimensionInputTitle={drawDraft.dimensionInputTitle ?? 'Type length, press Enter to place'}
            copyModifierEnabled={drawDraft.copyModifierEnabled}
            onDraftFinish={() => userDrawLayerRef.current?.finishDraft()}
            onDraftCancel={() => userDrawLayerRef.current?.cancelCommand?.()}
            onDimensionCommit={() => userDrawLayerRef.current?.commitTypedDimension()}
          />
        )}
        <Viewport ref={viewportRef}>
          <PanLayer style={{ transform: `translate(${pan.x}px, ${pan.y}px)` }}>
            <Stage ref={stageRef}>
            <PdfCanvas ref={canvasRef} />
            {markupsVisible && canvasSize.w > 0 && viewport && (
              <MarkupCanvas
                ref={markupCanvasRef}
                width={canvasSize.w}
                height={canvasSize.h}
                $interactive={interactiveVectors}
                style={{ visibility: overlaysPaused ? 'hidden' : 'visible' }}
                onClick={handleEntityClick}
              />
            )}
            {(docLoading || pageRendering) && (
              <LoadingHint>{docLoading ? 'Opening PDF…' : 'Rendering page…'}</LoadingHint>
            )}
            {ENGINE_DEBUG_ENABLED && canvasSize.w > 0 && viewport && (
              <Suspense fallback={null}>
                <EngineDebugLayer
                  ir={ir}
                  pageNum={pageNum}
                  viewport={viewport}
                  canvasSize={canvasSize}
                  overlaysPaused={overlaysPaused}
                />
              </Suspense>
            )}
            {labelsVisible && ir && canvasSize.w > 0 && viewport && (
              <>
                <OverlayCanvas
                  ref={overlayCanvasRef}
                  width={canvasSize.w}
                  height={canvasSize.h}
                  $interactive={interactiveOverlays}
                  style={{
                    visibility: overlaysPaused ? 'hidden' : 'visible',
                    pointerEvents: interactiveOverlays ? 'auto' : 'none',
                  }}
                  onClick={handleOverlayClick}
                />
                {highlightRect && !overlaysPaused && (
                  <HighlightSvg width={canvasSize.w} height={canvasSize.h}>
                    <rect
                      x={highlightRect.x}
                      y={highlightRect.y}
                      width={highlightRect.w}
                      height={highlightRect.h}
                      fill="none"
                      stroke="#e97024"
                      strokeWidth={3}
                      rx={3}
                    />
                  </HighlightSvg>
                )}
              </>
            )}
            {showUserLayer && canvasSize.w > 0 && viewport && (
              <UserDrawLayer
                ref={userDrawLayerRef}
                width={canvasSize.w}
                height={canvasSize.h}
                viewport={viewport}
                pageIndex={pageNum}
                sheetId={userDrawSheetId}
                hatId={activeHatId || 'architect'}
                drawTool={drawTool}
                arcDrawMode={arcDrawMode}
                userMarkups={userMarkups}
                selectedMarkupId={selectedUserMarkupId}
                snapSources={drawSnapSources}
                snapEnabled={userDrawActive || drawTool === DRAW_TOOLS.SELECT}
                onDraftChange={setDrawDraft}
                onCommandCancel={handleCommandCancel}
                onMarkupComplete={onUserMarkupComplete}
                onMarkupSelect={onUserMarkupSelect}
                onMarkupUpdate={onUserMarkupUpdate}
                onMarkupHistoryCheckpoint={onUserMarkupHistoryCheckpoint}
                onExtractedGeometryUpdate={onExtractedGeometryUpdate}
                selectedExtractedComponent={selectedExtractedComponent}
                modifyMode={modifyMode}
                onRotateUserMarkup={onRotateUserMarkup}
                onMirrorUserMarkup={onMirrorUserMarkup}
                onOffsetUserMarkup={onOffsetUserMarkup}
                onModifyModeChange={onModifyModeChange}
                scaleRatio={scaleRatio}
                dimensionInput={dimensionInput}
                onDimensionInputChange={setDimensionInput}
                interactive={!overlaysPaused}
                hidden={overlaysPaused}
              />
            )}
            {sheetFrameOverlay && canvasSize.w > 0 && (
              <SheetFrameSvg
                width={canvasSize.w}
                height={canvasSize.h}
                data-testid="sheet-frame-overlay"
              >
                <defs>
                  {/*
                    A HATCH, not a mask. Every element in this tile is a stroked
                    line over a transparent background, so the drawing stays
                    legible underneath. A solid fill would visually enact the
                    removal that this band is explicitly not authorized to
                    perform -- the reviewer would be looking at the outcome of a
                    decision they have not made yet.
                  */}
                  <pattern
                    id={SHEET_FRAME_HATCH_ID}
                    width="8"
                    height="8"
                    patternUnits="userSpaceOnUse"
                  >
                    <path d="M0,8 l8,-8" stroke="#e97024" strokeWidth="1" fill="none" />
                  </pattern>
                </defs>
                {sheetFrameOverlay.others.map(({ key, rect }) => (
                  <rect
                    key={key}
                    data-testid="sheet-frame-band-other"
                    x={rect.x}
                    y={rect.y}
                    width={rect.w}
                    height={rect.h}
                    fill="none"
                    stroke="#9ca3af"
                    strokeWidth={1}
                    strokeDasharray="4 4"
                    opacity={0.6}
                  />
                ))}
                <rect
                  data-testid="sheet-frame-band-selected"
                  x={sheetFrameOverlay.selectedRect.x}
                  y={sheetFrameOverlay.selectedRect.y}
                  width={sheetFrameOverlay.selectedRect.w}
                  height={sheetFrameOverlay.selectedRect.h}
                  fill={`url(#${SHEET_FRAME_HATCH_ID})`}
                  stroke="#e97024"
                  strokeWidth={2}
                />
              </SheetFrameSvg>
            )}
            {labelsVisible && previewComponentId && overlayComponents.length === 0 && overlaysReady && (
              <div style={{
                position: 'absolute',
                top: 12,
                left: 12,
                background: 'rgba(233,112,36,0.92)',
                color: '#fff',
                padding: '8px 12px',
                borderRadius: 8,
                fontSize: 12,
                maxWidth: 360,
                lineHeight: 1.45,
              }}>
                {pageRendering
                  ? 'Loading exemplar page…'
                  : `Exemplar label is on page ${(ir.components.find((c) => c.id === previewComponentId)?.pageIndex ?? 0) + 1} — switching view.`}
              </div>
            )}
            {labelsVisible && ir && overlayComponents.length === 0 && totalCount > 0 && !previewComponentId && overlaysReady && (
              <div style={{
                position: 'absolute',
                top: 12,
                left: 12,
                background: 'rgba(0,0,0,0.65)',
                color: '#fff',
                padding: '8px 12px',
                borderRadius: 8,
                fontSize: 12,
                maxWidth: 360,
                lineHeight: 1.45,
              }}>
                No labels on page {pageNum}.
                {ir.pagesExtracted != null && ir.pageCount != null && ir.pagesExtracted < ir.pageCount ? (
                  <>
                    {' '}
                    Extraction has only finished for {ir.pagesExtracted}/{ir.pageCount} pages —
                    labels appear on completed pages only.
                  </>
                ) : (
                  ' Try another page or adjust filters.'
                )}
              </div>
            )}
            </Stage>
          </PanLayer>
          {selectedUserMarkup && drawTool === DRAW_TOOLS.SELECT && (
            <ContextModifyBar
              visible
              modifyMode={modifyMode}
              canOffset={componentSupportsOffset(selectedUserMarkup)}
              onMove={() => onModifyModeChange?.(modifyMode === 'move' ? null : 'move')}
              onCopy={onCopyUserMarkup}
              onFreeRotate={() => onModifyModeChange?.(modifyMode === 'rotate' ? null : 'rotate')}
              onMirror={() => onModifyModeChange?.(modifyMode === 'mirror' ? null : 'mirror')}
              onOffsetModeToggle={() => onModifyModeChange?.(modifyMode === 'offset' ? null : 'offset')}
              onRotate={onRotateUserMarkup}
              onDelete={() => onUserMarkupDelete?.(selectedUserMarkup.id)}
            />
          )}
          {showFloatingInspector && selectedUserMarkup && (
            <FloatingMarkupInspector
              component={selectedUserMarkup}
              scaleRatio={scaleRatio}
              intentPrompt={selectedUserMarkup.id === intentPromptMarkupId}
              onSave={onUserMarkupSave}
              onDelete={onUserMarkupDelete}
              onDismiss={onDismissFloatingInspector}
              onOpenFullInspector={onOpenFullInspector}
            />
          )}
        </Viewport>
        <Toolbar>
          {onVisibilityLensChange && (
            <>
              {LENS_OPTIONS.map((opt) => (
                <ToolbarBtn
                  key={opt.id}
                  type="button"
                  $active={visibilityLens === opt.id}
                  onClick={() => onVisibilityLensChange(opt.id)}
                >
                  {opt.label}
                </ToolbarBtn>
              ))}
              {visibilityLens === VISIBILITY_LENSES.HAT && totalCount > 0 && (
                <span style={{ fontSize: 11, color: '#6b7280', padding: '0 4px' }}>
                  {visibleCount}/{totalCount}
                </span>
              )}
              <ToolbarDivider />
            </>
          )}
          {numPages > 1 && (
            <>
              <ToolbarBtn type="button" disabled={pageNum <= 1} onClick={() => goToPage(pageNum - 1)} aria-label="Previous page">‹</ToolbarBtn>
              <span style={{ fontSize: 12, alignSelf: 'center' }}>{pageNum} / {numPages}</span>
              <ToolbarBtn type="button" disabled={pageNum >= numPages} onClick={() => goToPage(pageNum + 1)} aria-label="Next page">›</ToolbarBtn>
              <ToolbarDivider />
            </>
          )}
          <ToolbarBtn type="button" onClick={zoomOut} aria-label="Zoom out">−</ToolbarBtn>
          <ZoomReadout>{zoomPercent}%</ZoomReadout>
          <ToolbarBtn type="button" onClick={zoomIn} aria-label="Zoom in">+</ToolbarBtn>
          <ToolbarBtn type="button" onClick={zoomFit} aria-label="Fit to width">Fit</ToolbarBtn>
        </Toolbar>
      </ViewerCanvas>
    </ViewerRoot>
  );
}
